// ─────────────────────────────────────────────────────────────────────
// Archive bulk downloader (web)
// ─────────────────────────────────────────────────────────────────────
//
// Mirror of App/Hexponents/Hexponents/ArchiveDownloader.swift. Walks
// every (date, difficulty) pair from MIN_ARCHIVE_DATE through today
// (America/New_York), fetches /api/v1/puzzles/:date/:difficulty, and
// stores each into the pinned IDB tier so the user can play the entire
// past archive offline.
//
// Throttle: 4 concurrent fetches. Skip rows already pinned (idempotent
// across re-runs).  Cancellation isn't wired — the user reloading the
// page is sufficient, and the in-flight writes that complete before
// reload are kept (each write is its own transaction).
//
// `bearer` is accepted to mirror the iOS signature; the web client
// authenticates via httpOnly cookies, so `apiFetch` already does the
// right thing and the param is unused. Kept in the type so future
// caller refactors don't need to touch this signature.

import { apiFetch } from './api';
import { dailyDateString } from '../generator';
import type { Difficulty, Puzzle } from '../types';
import { isPinned, pinnedByteCount, pinnedCount, writePinned } from './offlineCache';

export type ArchiveDlState =
  | { kind: 'idle' }
  | { kind: 'downloading'; done: number; total: number }
  | { kind: 'complete'; count: number; bytes: number };

const MIN_ARCHIVE_DATE = '2026-01-01';
const DIFFICULTIES: Difficulty[] = ['Easy', 'Medium', 'Hard', 'Expert'];
const MAX_CONCURRENT = 4;

interface Job { date: string; difficulty: Difficulty }

/// First-load helper for the UI — if the user has already downloaded
/// a non-zero number of puzzles in a previous session, surface that
/// as `.complete` so the section doesn't render the "Download" CTA.
export async function summarizePinned(): Promise<ArchiveDlState> {
  const count = await pinnedCount();
  if (count <= 0) return { kind: 'idle' };
  const bytes = await pinnedByteCount();
  return { kind: 'complete', count, bytes };
}

export async function runArchiveDownload(
  _bearer: string | null,
  onState: (s: ArchiveDlState) => void,
): Promise<void> {
  const dates = allArchiveDates();
  const jobs: Job[] = [];
  for (const date of dates) {
    for (const difficulty of DIFFICULTIES) jobs.push({ date, difficulty });
  }
  const total = jobs.length;
  if (total === 0) {
    onState({ kind: 'complete', count: 0, bytes: 0 });
    return;
  }

  let done = 0;
  onState({ kind: 'downloading', done, total });

  let cursor = 0;
  const workers: Promise<void>[] = [];

  const next = (): Job | null => {
    if (cursor >= jobs.length) return null;
    const j = jobs[cursor];
    cursor += 1;
    return j;
  };

  const runWorker = async () => {
    for (;;) {
      const job = next();
      if (!job) return;
      try {
        // Idempotent: skip rows already in the cache. Avoids re-fetching
        // everything on a re-run after partial completion.
        if (await isPinned(job.date, job.difficulty)) {
          done += 1;
          onState({ kind: 'downloading', done, total });
          continue;
        }
        await fetchAndPin(job.date, job.difficulty);
      } catch {
        // 401/403/5xx — skip silently and continue. The summary line
        // will reflect whatever did make it in.
      } finally {
        // Count this slot as done regardless of fetch outcome; this is
        // a one-shot UI and the user can re-run to top up.
        // (We already counted skipped rows above; only count fetched
        // ones here.)
      }
      done += 1;
      onState({ kind: 'downloading', done, total });
    }
  };

  for (let i = 0; i < Math.min(MAX_CONCURRENT, total); i++) {
    workers.push(runWorker());
  }
  await Promise.all(workers);

  const finalCount = await pinnedCount();
  const finalBytes = await pinnedByteCount();
  onState({ kind: 'complete', count: finalCount, bytes: finalBytes });
}

/// Single (date, difficulty) fetch + pin. Surfaces failures by throwing
/// so the worker loop's catch can swallow them.
async function fetchAndPin(date: string, difficulty: Difficulty): Promise<void> {
  const res = await apiFetch(`/puzzles/${date}/${difficulty}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const puzzle = (await res.json()) as Puzzle;
  await writePinned(date, difficulty, puzzle);
}

/// Today (ET) → MIN_ARCHIVE_DATE inclusive. Order doesn't matter for
/// the downloader since we're filling a cache, but having today first
/// means a partial run still covers the most-likely-to-be-played dates.
function allArchiveDates(): string[] {
  const today = dailyDateString();
  const out: string[] = [];
  let cursor = today;
  while (cursor >= MIN_ARCHIVE_DATE) {
    out.push(cursor);
    cursor = shiftDate(cursor, -1);
    if (out.length > 800) break; // safety: ~2 years
  }
  return out;
}

function shiftDate(s: string, delta: number): string {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}

/// Human-readable byte formatter. Mirrors iOS's ByteCountFormatter
/// output for the summary line ("120 puzzles · 600 KB").
export function humanReadableBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
